import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Button,
    CircularProgress,
    Chip,
    Divider,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    FormControlLabel,
    Switch
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TranslateIcon from '@mui/icons-material/Translate';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import { theme } from '../../../theme';
import { superadminService } from '../../../services/superadmin.service';
import TranslationValidationModal from '../../pro/salon/TranslationValidationModal';
import ServiceConfirmationModeField from '../../pro/salon/service/ServiceConfirmationModeField';

const LANGUAGES = ['fr', 'en', 'ar'];

const buildTranslationsState = (translations = {}) => ({
    fr: translations?.fr || '',
    en: translations?.en || '',
    ar: translations?.ar || '',
});

const sanitizeTranslations = (translations = {}) => (
    Object.fromEntries(
        Object.entries(translations)
            .map(([language, value]) => [language, (value || '').trim()])
            .filter(([, value]) => value)
    )
);

export const getServiceBookingState = (service) => {
    if (service.client_booking_disabled) {
        return { label: 'Prise de rendez-vous désactivée', color: 'default' };
    }
    if (service.booking_confirmation_mode === 'manual') {
        return { label: 'Confirmation manuelle', color: 'warning' };
    }
    return { label: 'Rendez-vous automatique', color: 'success' };
};

const SalonServices = ({ salonId }) => {
    const { i18n } = useTranslation();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const [deleteType, setDeleteType] = useState(null); // 'service' or 'category'
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

    // Category Creation State
    const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
    const [categoryForm, setCategoryForm] = useState({
        name: '',
        description: '',
        name_translations: buildTranslationsState(),
        description_translations: buildTranslationsState(),
        useExisting: true,
        selectedCategories: []
    });
    const [defaultCategories, setDefaultCategories] = useState([]);
    const [savingCategory, setSavingCategory] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [categoryTranslationOpen, setCategoryTranslationOpen] = useState(false);
    const [categoryTranslationLoading, setCategoryTranslationLoading] = useState(false);
    const [translationCategory, setTranslationCategory] = useState(null);

    // Service Creation State
    const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
    const [serviceForm, setServiceForm] = useState({
        category: '',
        name: '',
        description: '',
        name_translations: buildTranslationsState(),
        description_translations: buildTranslationsState(),
        price: '',
        prix_fixe: true,
        client_booking_disabled: false,
        booking_confirmation_mode: 'automatic',
        duration: ''
    });
    const [savingService, setSavingService] = useState(false);
    const [serviceTranslationOpen, setServiceTranslationOpen] = useState(false);
    const [serviceTranslationLoading, setServiceTranslationLoading] = useState(false);
    const [translationService, setTranslationService] = useState(null);

    const showToast = (message, severity = 'success') => {
        setToast({ open: true, message, severity });
    };

    const handleCloseToast = (event, reason) => {
        if (reason === 'clickaway') return;
        setToast({ ...toast, open: false });
    };

    const loadServices = async () => {
        try {
            setLoading(true);

            // Fetch all categories for this salon
            const catsRes = await superadminService.getSalonCategories(salonId);
            const salonCategories = catsRes.data || [];

            // Fetch services grouped by category
            const servicesRes = await superadminService.getSalonServices(salonId);
            const servicesByCat = servicesRes.data || [];

            // Merge logic
            const mergedCategories = salonCategories.map(cat => {
                // Find services for this category
                const serviceGroup = servicesByCat.find(g => g.category_id === cat.category_id);
                return {
                    ...cat,
                    category_name: cat.localized_name || cat.name, // Use localized name if available
                    services: serviceGroup ? serviceGroup.services : []
                };
            });

            // Handle uncategorized services (category_id is null)
            const uncategorizedGroup = servicesByCat.find(g => g.category_id === null);
            if (uncategorizedGroup && uncategorizedGroup.services.length > 0) {
                mergedCategories.push({
                    category_id: 'uncategorized', // special ID
                    category_name: 'Non classé',
                    name: 'Non classé',
                    services: uncategorizedGroup.services,
                    is_uncategorized: true // Flag to prevent deletion of "category"
                });
            }

            setCategories(mergedCategories);
        } catch (error) {
            console.error("Failed to load services", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (salonId) {
            loadServices();
        }
    }, [salonId, i18n.language]);

    const handleRequestDelete = (id, type) => {
        setItemToDelete(id);
        setDeleteType(type);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!itemToDelete || !deleteType) return;
        try {
            setDeleting(itemToDelete);

            if (deleteType === 'service') {
                await superadminService.deleteService(itemToDelete);
                showToast('Prestation supprimée.', 'success');
            } else if (deleteType === 'category') {
                await superadminService.deleteCategory(itemToDelete);
                showToast('Catégorie supprimée.', 'success');
            }

            await loadServices();
        } catch (error) {
            console.error(`Failed to delete ${deleteType}`, error);
            const errorMsg = error.response?.data?.error || `Erreur lors de la suppression de ${deleteType === 'service' ? 'la prestation' : 'la catégorie'}.`;
            showToast(errorMsg, 'error');
        } finally {
            setDeleting(null);
            setDeleteDialogOpen(false);
            setItemToDelete(null);
            setDeleteType(null);
        }
    };

    const handleOpenCategoryDialog = async () => {
        setEditMode(false);
        setEditingId(null);
        setCategoryForm({
            name: '',
            description: '',
            name_translations: buildTranslationsState(),
            description_translations: buildTranslationsState(),
            useExisting: true,
            selectedCategories: []
        });
        setCategoryDialogOpen(true);
        try {
            const res = await superadminService.getDefaultCategories();
            const allDefaults = res.data || [];

            // Filter out categories that already exist in the salon
            // We check against the currently loaded categories for this salon
            const existingNames = new Set(categories.map(c => c.name ? c.name.toLowerCase() : ''));
            const availableDefaults = allDefaults.filter(d => !existingNames.has(d.name.toLowerCase()));

            setDefaultCategories(availableDefaults);
        } catch (error) {
            console.error("Failed to load default categories", error);
        }
    };

    // Reload default categories when language changes
    useEffect(() => {
        if (categoryDialogOpen) {
            handleOpenCategoryDialog();
        }
    }, [i18n.language]);

    const handleEditCategory = (category) => {
        setEditMode(true);
        setEditingId(category.category_id);
        setCategoryForm({
            name: category.name,
            description: category.description || '',
            name_translations: buildTranslationsState(category.name_translations),
            description_translations: buildTranslationsState(category.description_translations),
            useExisting: false,
            selectedCategories: []
        });
        setCategoryDialogOpen(true);
    };

    const handleOpenCategoryTranslations = async (category) => {
        setTranslationCategory(category);
        setCategoryTranslationOpen(true);
        setCategoryTranslationLoading(true);

        try {
            const response = await superadminService.retrieveCategory(category.category_id);
            setTranslationCategory(response.data);
        } catch (error) {
            console.error('Failed to load category translations', error);
            showToast('Impossible de charger les traductions de la catégorie.', 'error');
        } finally {
            setCategoryTranslationLoading(false);
        }
    };

    const handleSaveCategoryTranslations = async (editedTranslations) => {
        if (!translationCategory) return;

        try {
            setSavingCategory(true);
            await superadminService.updateCategory(translationCategory.category_id, {
                name_translations: sanitizeTranslations(editedTranslations.name),
                description_translations: sanitizeTranslations(editedTranslations.description)
            });
            setCategoryTranslationOpen(false);
            setTranslationCategory(null);
            showToast('Traductions de la catégorie mises à jour.', 'success');
            await loadServices();
        } catch (error) {
            console.error('Failed to update category translations', error);
            showToast('Erreur lors de la mise à jour des traductions de la catégorie.', 'error');
        } finally {
            setSavingCategory(false);
        }
    };

    const handleSaveCategory = async () => {
        try {
            setSavingCategory(true);

            if (editMode) {
                await superadminService.updateCategory(editingId, {
                    name: categoryForm.name,
                    description: categoryForm.description,
                    name_translations: sanitizeTranslations(categoryForm.name_translations),
                    description_translations: sanitizeTranslations(categoryForm.description_translations)
                });
                showToast('Catégorie mise à jour avec succès.', 'success');
            } else if (categoryForm.useExisting) {
                // Create from existing/default categories
                const promises = categoryForm.selectedCategories.map(catId => {
                    const cat = defaultCategories.find(c => c.category_id === catId);
                    return superadminService.createCategory({
                        salon: salonId,
                        name: cat.name,
                        description: cat.description,
                        name_translations: cat.name_translations || {},
                        description_translations: cat.description_translations || {}
                    });
                });
                await Promise.all(promises);
            } else {
                // Create custom category
                await superadminService.createCategory({
                    salon: salonId,
                    name: categoryForm.name,
                    description: categoryForm.description,
                    name_translations: sanitizeTranslations(categoryForm.name_translations),
                    description_translations: sanitizeTranslations(categoryForm.description_translations)
                });
            }

            showToast(editMode ? 'Catégorie mise à jour.' : 'Catégorie(s) créée(s) avec succès.', 'success');
            setCategoryDialogOpen(false);
            loadServices();
        } catch (error) {
            console.error("Failed to save category", error);
            showToast(editMode ? 'Erreur lors de la modification de la catégorie.' : 'Erreur lors de la création de la catégorie.', 'error');
        } finally {
            setSavingCategory(false);
        }
    };

    const handleOpenServiceDialog = (preSelectedCategory = '') => {
        setEditMode(false);
        setEditingId(null);
        setServiceForm({
            category: preSelectedCategory || '',
            name: '',
            description: '',
            name_translations: buildTranslationsState(),
            description_translations: buildTranslationsState(),
            price: '',
            prix_fixe: true,
            client_booking_disabled: false,
            booking_confirmation_mode: 'automatic',
            duration: ''
        });
        setServiceDialogOpen(true);
    };

    const handleOpenServiceTranslations = async (service) => {
        setTranslationService(service);
        setServiceTranslationOpen(true);
        setServiceTranslationLoading(true);

        try {
            const response = await superadminService.retrieveService(service.service_id);
            setTranslationService(response.data);
        } catch (error) {
            console.error('Failed to load service translations', error);
            showToast('Impossible de charger les traductions de la prestation.', 'error');
        } finally {
            setServiceTranslationLoading(false);
        }
    };

    const handleSaveServiceTranslations = async (editedTranslations) => {
        if (!translationService) return;

        try {
            setSavingService(true);
            await superadminService.updateService(translationService.service_id, {
                name_translations: sanitizeTranslations(editedTranslations.name),
                description_translations: sanitizeTranslations(editedTranslations.description),
                translations_auto: false,
            });
            setServiceTranslationOpen(false);
            setTranslationService(null);
            showToast('Traductions de la prestation mises à jour.', 'success');
            await loadServices();
        } catch (error) {
            console.error('Failed to update service translations', error);
            showToast('Erreur lors de la mise à jour des traductions de la prestation.', 'error');
        } finally {
            setSavingService(false);
        }
    };

    const handleEditService = (service, categoryId) => {
        setEditMode(true);
        setEditingId(service.service_id);
        setServiceForm({
            category: categoryId || service.category_id || (service.category ? service.category.id : ''),
            name: service.name,
            description: service.description || '',
            name_translations: buildTranslationsState(service.name_translations),
            description_translations: buildTranslationsState(service.description_translations),
            price: service.price,
            prix_fixe: service.prix_fixe !== undefined && service.prix_fixe !== null ? service.prix_fixe : true,
            client_booking_disabled: !!service.client_booking_disabled,
            booking_confirmation_mode: service.booking_confirmation_mode || 'automatic',
            duration: service.duration
        });
        setServiceDialogOpen(true);
    };

    const handleSaveService = async () => {
        try {
            setSavingService(true);

            // Convert 'uncategorized' to null for backend
            const categoryId = serviceForm.category === 'uncategorized' ? null : serviceForm.category;

            if (editMode) {
                await superadminService.updateService(editingId, {
                    category: categoryId,
                    name: serviceForm.name,
                    description: serviceForm.description,
                    name_translations: sanitizeTranslations(serviceForm.name_translations),
                    description_translations: sanitizeTranslations(serviceForm.description_translations),
                    translations_auto: false,
                    price: parseFloat(serviceForm.price),
                    prix_fixe: !!serviceForm.prix_fixe,
                    client_booking_disabled: !!serviceForm.client_booking_disabled,
                    booking_confirmation_mode: serviceForm.booking_confirmation_mode,
                    duration: parseInt(serviceForm.duration, 10)
                });
                showToast('Prestation mise à jour avec succès.', 'success');
            } else {
                await superadminService.createService({
                    salon: salonId,
                    category: categoryId,
                    name: serviceForm.name,
                    description: serviceForm.description,
                    name_translations: sanitizeTranslations(serviceForm.name_translations),
                    description_translations: sanitizeTranslations(serviceForm.description_translations),
                    translations_auto: false,
                    price: parseFloat(serviceForm.price),
                    prix_fixe: !!serviceForm.prix_fixe,
                    client_booking_disabled: !!serviceForm.client_booking_disabled,
                    booking_confirmation_mode: serviceForm.booking_confirmation_mode,
                    duration: parseInt(serviceForm.duration, 10)
                });
                showToast('Prestation ajoutée avec succès.', 'success');
            }

            setServiceDialogOpen(false);
            await loadServices();
        } catch (error) {
            console.error("Failed to save service", error);
            showToast(editMode ? 'Erreur lors de la modification de la prestation.' : 'Erreur lors de la création de la prestation.', 'error');
        } finally {
            setSavingService(false);
        }
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: 3
            }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Prestations
                </Typography>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                    <Button
                        variant="outlined"
                        startIcon={<LibraryAddIcon />}
                        onClick={handleOpenCategoryDialog}
                        sx={{
                            fontWeight: 700,
                            borderRadius: theme.borderRadius.lg,
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 2 },
                            flex: { xs: 1, sm: 'none' }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Nouvelle </Box>Catégorie
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => handleOpenServiceDialog()}
                        sx={{
                            fontWeight: 700,
                            borderRadius: theme.borderRadius.lg,
                            textTransform: 'none',
                            px: { xs: 1.5, sm: 2 },
                            flex: { xs: 1, sm: 'none' }
                        }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Ajouter une </Box>Prestation
                    </Button>
                </Box>
            </Box>

            {loading && categories.length === 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : categories.length === 0 ? (
                <Box
                    sx={{
                        p: 6,
                        textAlign: 'center',
                        bgcolor: theme.colors.background.secondary,
                        borderRadius: theme.borderRadius.xl,
                        border: `1px dashed ${theme.colors.border.light}`
                    }}
                >
                    <Typography variant="body1" sx={{ color: theme.colors.text.secondary, fontWeight: 500 }}>
                        Aucune prestation disponible.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {categories.map((cat) => (
                        <Accordion
                            key={cat.category_id}
                            defaultExpanded
                            sx={{
                                borderRadius: `${theme.borderRadius.lg} !important`,
                                border: `1px solid ${theme.colors.border.light}`,
                                boxShadow: theme.boxShadow.sm,
                                '&:before': { display: 'none' }, // Remove default before pseudo-element
                                overflow: 'hidden'
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{ bgcolor: theme.colors.background.secondary, fontWeight: 700 }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2 }}>
                                    <Typography sx={{ fontWeight: 700 }}>{cat.category_name} ({cat.services.length})</Typography>
                                    {!cat.is_uncategorized && (
                                        <Box onClick={(e) => e.stopPropagation()}>
                                            <IconButton
                                                size="small"
                                                aria-label={`Traduire la catégorie ${cat.name}`}
                                                onClick={() => handleOpenCategoryTranslations(cat)}
                                                sx={{
                                                    mr: 1,
                                                    color: theme.colors.text.tertiary,
                                                    '&:hover': { color: theme.colors.secondary[500], bgcolor: theme.colors.secondary[50] }
                                                }}
                                            >
                                                <TranslateIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label={`Modifier la catégorie ${cat.name}`}
                                                onClick={() => handleEditCategory(cat)}
                                                sx={{
                                                    mr: 1,
                                                    color: theme.colors.text.tertiary,
                                                    '&:hover': { color: theme.colors.primary.main, bgcolor: theme.colors.primary[50] }
                                                }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label={`Supprimer la catégorie ${cat.name}`}
                                                onClick={() => handleRequestDelete(cat.category_id, 'category')}
                                                disabled={deleting === cat.category_id}
                                                sx={{
                                                    color: theme.colors.text.tertiary,
                                                    '&:hover': { color: theme.colors.error[500], bgcolor: theme.colors.error[50] }
                                                }}
                                            >
                                                {deleting === cat.category_id ? <CircularProgress size={20} /> : <DeleteIcon />}
                                            </IconButton>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                {cat.services.length > 0 ? (
                                    <List disablePadding>
                                        {cat.services.map((service, index) => (
                                            <React.Fragment key={service.service_id}>
                                                <ListItem
                                                    sx={{
                                                        py: 2,
                                                        px: 3,
                                                        '&:hover': { bgcolor: theme.colors.background.tertiary }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                        {/* Column 1: Name and Metadata */}
                                                        <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                                                            <Typography variant="h5" sx={{ fontWeight: 800, fontSize: '1.4rem', color: theme.colors.text.primary, lineHeight: 1.2, mb: 0.5 }}>
                                                                {service.localized_name || service.name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: theme.colors.text.secondary }}>
                                                                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{service.duration} min</Typography>
                                                                </Box>
                                                                {(service.localized_description || service.description) && (
                                                                    <Typography variant="body2" sx={{ color: theme.colors.text.tertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
                                                                        {service.localized_description || service.description}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>

                                                        {/* Column 2: Price */}
                                                        <Box sx={{ mx: 2, textAlign: 'right', minWidth: '80px' }}>
                                                            <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1.1rem', color: theme.colors.primary[600] }}>
                                                                {service.price} DA
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={service.prix_fixe ? 'Prix fixe' : 'Prix variable'}
                                                                sx={{ mt: 0.5, fontWeight: 700 }}
                                                            />
                                                            <Chip
                                                                size="small"
                                                                {...getServiceBookingState(service)}
                                                                sx={{ mt: 0.5, fontWeight: 700 }}
                                                            />
                                                        </Box>

                                                        {/* Column 3: Actions */}
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Traduire la prestation ${service.name}`}
                                                                onClick={() => handleOpenServiceTranslations(service)}
                                                                sx={{
                                                                    color: theme.colors.text.tertiary,
                                                                    '&:hover': { color: theme.colors.secondary[500], bgcolor: theme.colors.secondary[50] }
                                                                }}
                                                            >
                                                                <TranslateIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Modifier la prestation ${service.name}`}
                                                                onClick={() => handleEditService(service, cat.category_id)}
                                                                sx={{
                                                                    color: theme.colors.text.tertiary,
                                                                    '&:hover': { color: theme.colors.primary.main, bgcolor: theme.colors.primary[50] }
                                                                }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                aria-label={`Supprimer la prestation ${service.name}`}
                                                                onClick={() => handleRequestDelete(service.service_id, 'service')}
                                                                disabled={deleting === service.service_id}
                                                                sx={{
                                                                    color: theme.colors.text.tertiary,
                                                                    '&:hover': { color: theme.colors.error[500], bgcolor: theme.colors.error[50] }
                                                                }}
                                                            >
                                                                {deleting === service.service_id ? <CircularProgress size={20} /> : <DeleteIcon />}
                                                            </IconButton>
                                                        </Box>
                                                    </Box>
                                                </ListItem>
                                                {index < cat.services.length - 1 && <Divider component="li" />}
                                            </React.Fragment>
                                        ))}
                                    </List>
                                ) : (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Aucune prestation dans cette catégorie.
                                        </Typography>
                                        <Button
                                            size="small"
                                            startIcon={<AddCircleOutlineIcon />}
                                            onClick={() => handleOpenServiceDialog(cat.category_id)}
                                            sx={{ mt: 1 }}
                                        >
                                            Ajouter une prestation
                                        </Button>
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 800 }}>Confirmer la suppression</DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteType === 'category'
                            ? "Voulez-vous vraiment supprimer cette catégorie ? Toutes les prestations associées seront également supprimées."
                            : "Voulez-vous vraiment supprimer cette prestation ?"}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
                    <Button variant="contained" color="error" onClick={handleConfirmDelete} disabled={deleting !== null}>
                        {deleting ? <CircularProgress size={24} color="inherit" /> : 'Supprimer'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Category Dialog */}
            <Dialog open={categoryDialogOpen} onClose={() => setCategoryDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>{editMode ? 'Modifier la Catégorie' : 'Nouvelle Catégorie'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {!editMode && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={categoryForm.useExisting}
                                        onChange={(e) => setCategoryForm({ ...categoryForm, useExisting: e.target.checked })}
                                    />
                                }
                                label="Utiliser une catégorie prédéfinie"
                            />
                        )}

                        {categoryForm.useExisting && !editMode ? (
                            <FormControl fullWidth>
                                <InputLabel>Catégories prédéfinies</InputLabel>
                                <Select
                                    multiple
                                    value={categoryForm.selectedCategories}
                                    label="Catégories prédéfinies"
                                    onChange={(e) => setCategoryForm({ ...categoryForm, selectedCategories: e.target.value })}
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => {
                                                const cat = defaultCategories.find(c => c.category_id === value);
                                                return <Chip key={value} label={cat?.localized_name || cat?.name} size="small" />;
                                            })}
                                        </Box>
                                    )}
                                >
                                    {defaultCategories.map((cat) => (
                                        <MenuItem key={cat.category_id} value={cat.category_id}>
                                            {cat.localized_name || cat.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            <>
                                <TextField
                                    label="Nom de la catégorie"
                                    fullWidth
                                    value={categoryForm.name}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                                />
                                <TextField
                                    label="Description"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={categoryForm.description}
                                    onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                                />
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                                    Traductions du nom
                                </Typography>
                                {LANGUAGES.map((language) => (
                                    <TextField
                                        key={`category-name-${language}`}
                                        label={`Nom (${language.toUpperCase()})`}
                                        fullWidth
                                        value={categoryForm.name_translations?.[language] || ''}
                                        onChange={(e) => setCategoryForm({
                                            ...categoryForm,
                                            name_translations: {
                                                ...categoryForm.name_translations,
                                                [language]: e.target.value,
                                            }
                                        })}
                                    />
                                ))}
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                                    Traductions de la description
                                </Typography>
                                {LANGUAGES.map((language) => (
                                    <TextField
                                        key={`category-description-${language}`}
                                        label={`Description (${language.toUpperCase()})`}
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={categoryForm.description_translations?.[language] || ''}
                                        onChange={(e) => setCategoryForm({
                                            ...categoryForm,
                                            description_translations: {
                                                ...categoryForm.description_translations,
                                                [language]: e.target.value,
                                            }
                                        })}
                                    />
                                ))}
                            </>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCategoryDialogOpen(false)}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveCategory}
                        disabled={(categoryForm.useExisting && !editMode && categoryForm.selectedCategories.length === 0) || (!categoryForm.useExisting && !categoryForm.name) || savingCategory}
                    >
                        {savingCategory ? <CircularProgress size={24} /> : (editMode ? 'Enregistrer' : 'Créer')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Service Dialog */}
            <Dialog open={serviceDialogOpen} onClose={() => setServiceDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle sx={{ fontWeight: 800 }}>{editMode ? 'Modifier la Prestation' : 'Ajouter une Prestation'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="service-category-label">Catégorie</InputLabel>
                            <Select
                                labelId="service-category-label"
                                id="service-category"
                                value={serviceForm.category}
                                label="Catégorie"
                                onChange={(e) => setServiceForm({ ...serviceForm, category: e.target.value })}
                            >
                                {categories.map((cat) => (
                                    <MenuItem key={cat.category_id} value={cat.category_id}>
                                        {cat.localized_name || cat.category_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Nom de la prestation"
                            fullWidth
                            value={serviceForm.name}
                            onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Prix"
                                fullWidth
                                type="number"
                                value={serviceForm.price}
                                onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">DA</InputAdornment>,
                                }}
                            />
                            <TextField
                                label="Durée (min)"
                                fullWidth
                                type="number"
                                value={serviceForm.duration}
                                onChange={(e) => setServiceForm({ ...serviceForm, duration: e.target.value })}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">min</InputAdornment>,
                                }}
                            />
                        </Box>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!serviceForm.prix_fixe}
                                    onChange={(e) => setServiceForm({ ...serviceForm, prix_fixe: e.target.checked })}
                                />
                            }
                            label="Prix fixe"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!serviceForm.client_booking_disabled}
                                    onChange={(e) => setServiceForm({ ...serviceForm, client_booking_disabled: e.target.checked })}
                                />
                            }
                            label="Désactiver la prise de rendez-vous"
                        />
                        <ServiceConfirmationModeField
                            value={serviceForm.booking_confirmation_mode}
                            onChange={(value) => setServiceForm({ ...serviceForm, booking_confirmation_mode: value })}
                            hidden={!!serviceForm.client_booking_disabled}
                            labels={{
                                title: 'Validation du rendez-vous',
                                automatic: 'Confirmation automatique',
                                manual: 'Validation par le salon',
                            }}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            rows={3}
                            value={serviceForm.description}
                            onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                        />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                            Traductions du nom
                        </Typography>
                        {LANGUAGES.map((language) => (
                            <TextField
                                key={`service-name-${language}`}
                                label={`Nom (${language.toUpperCase()})`}
                                fullWidth
                                value={serviceForm.name_translations?.[language] || ''}
                                onChange={(e) => setServiceForm({
                                    ...serviceForm,
                                    name_translations: {
                                        ...serviceForm.name_translations,
                                        [language]: e.target.value,
                                    }
                                })}
                            />
                        ))}
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                            Traductions de la description
                        </Typography>
                        {LANGUAGES.map((language) => (
                            <TextField
                                key={`service-description-${language}`}
                                label={`Description (${language.toUpperCase()})`}
                                fullWidth
                                multiline
                                rows={2}
                                value={serviceForm.description_translations?.[language] || ''}
                                onChange={(e) => setServiceForm({
                                    ...serviceForm,
                                    description_translations: {
                                        ...serviceForm.description_translations,
                                        [language]: e.target.value,
                                    }
                                })}
                            />
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setServiceDialogOpen(false)}>Annuler</Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveService}
                        disabled={!serviceForm.name || !serviceForm.category || !serviceForm.price || !serviceForm.duration || savingService}
                    >
                        {savingService ? <CircularProgress size={24} /> : (editMode ? 'Enregistrer' : 'Ajouter')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={handleCloseToast}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
                    {toast.message}
                </Alert>
            </Snackbar>
            <TranslationValidationModal
                open={categoryTranslationOpen}
                onClose={() => {
                    setCategoryTranslationOpen(false);
                    setTranslationCategory(null);
                }}
                onValidate={handleSaveCategoryTranslations}
                salonName={translationCategory?.name || ''}
                salonDescription={translationCategory?.description || ''}
                isLoading={categoryTranslationLoading}
                translations={{
                    name: translationCategory?.name_translations || {},
                    description: translationCategory?.description_translations || {},
                }}
            />
            <TranslationValidationModal
                open={serviceTranslationOpen}
                onClose={() => {
                    setServiceTranslationOpen(false);
                    setTranslationService(null);
                }}
                onValidate={handleSaveServiceTranslations}
                salonName={translationService?.name || ''}
                salonDescription={translationService?.description || ''}
                isLoading={serviceTranslationLoading}
                translations={{
                    name: translationService?.name_translations || {},
                    description: translationService?.description_translations || {},
                }}
            />
        </Box>
    );
};

export default SalonServices;
